import { create } from "zustand";
import { createJSONStorage, persist, subscribeWithSelector } from "zustand/middleware";
import { z } from "zod";
import {
  Order,
  Organisation,
  Owner,
  OwnerType,
  User,
} from "@/api/SolarCloudApi";
import { addressSchema } from "./schema";
import { setOrderUnitsCookie } from "@/util/cookies";
import { getBuyPanelsBasePath, getBuyPanelsRootSlug } from "./route";

type Address = z.infer<typeof addressSchema>;

type BuyPanelsData = {
  order: Order;
  email: string;
  password: string;
  user: User;
  ownerType: OwnerType;
  organisation: Organisation;
  address: Address;
  owner: Owner;
};

export type Step = {
  number: number;
  title: string;
  description: string;
  slug: string;
  isCompleted: boolean;
};

const steps: Step[] = [
  {
    number: 1,
    title: "Choice",
    description: "How many panels?",
    slug: "buypanels",
    isCompleted: false,
  },
  {
    number: 2,
    title: "Checkout",
    description: "Complete your purchase",
    slug: "ownership",
    isCompleted: false,
  },
];

type BuyPanelsStore = {
  // Data state
  data: Partial<BuyPanelsData>;
  setData: (data: Partial<BuyPanelsData>) => void;
  setOwnerType: (ownerType: OwnerType) => void;

  // Steps state
  steps: Step[];
  getNextStep: () => string;
  getPreviousStep: () => string;
  setStepComplete: (slug: string) => void;
  setStepIncomplete: (slug: string) => void;
  getStepNumber: (slug: string) => number;

  // Cart state
  reset: () => void;
  createdAt: Date;
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
  billingOption?: "oneoff" | "recurring";
  setBillingOption: (option: "oneoff" | "recurring") => void;

  // Payment Frequency State
  paymentType: "once" | "recurring";
  setPaymentType: (type: "once" | "recurring") => void;
  resetPaymentFrequency: () => void;
  frequency: string;
  setFrequency: (frequency: string) => void;
  endCondition: "no-end-date" | "certain-date" | "number-of-payments";
  setEndCondition: (condition: "no-end-date" | "certain-date" | "number-of-payments") => void;
  endDate: string;
  setEndDate: (date: string) => void;
  paymentCount: string;
  setPaymentCount: (count: string) => void;
};

export const useBuyPanelsStore = create<BuyPanelsStore>()(
  subscribeWithSelector(
    persist(
    (set, get) => ({
      // Data state
      data: {},
      setData: (data) => set((state) => ({ data: { ...state.data, ...data } })),
      setOwnerType: (ownerType: OwnerType) =>
        set((state) => ({ data: { ...state.data, ownerType } })),
      // Steps state
      steps,
      getNextStep: () => {
        const { steps } = get();
        const pathSegments = window.location.pathname.split("/").filter(Boolean);
        const currentPath = pathSegments.pop() || "";

        const basePath = getBuyPanelsBasePath();
        const rootSlug = getBuyPanelsRootSlug();

        let currentStepIndex = steps.findIndex((step) => step.slug === currentPath);

        // If we're on the index route, treat it as the first step
        if (currentStepIndex === -1 && currentPath === rootSlug) {
          currentStepIndex = 0;
        }

        if (currentPath === "ownership") {
          return `${basePath}/confirmation`;
        }

        if (currentStepIndex >= 0 && currentStepIndex < steps.length - 1) {
          return `${basePath}/${steps[currentStepIndex + 1].slug}`;
        }
        return `${basePath}/`;
      },
      getPreviousStep: () => {
        const { steps } = get();
        const pathSegments = window.location.pathname.split("/").filter(Boolean);
        const currentPath = pathSegments.pop() || "";

        const basePath = getBuyPanelsBasePath();
        const rootSlug = getBuyPanelsRootSlug();

        let currentStepIndex = steps.findIndex((step) => step.slug === currentPath);

        // If we're on the index route, treat it as the first step
        if (currentStepIndex === -1 && currentPath === rootSlug) {
          currentStepIndex = 0;
        }

        if (currentStepIndex > 0) {
          const previousStep = steps[currentStepIndex - 1];
          // If previous step is the first step (buypanels), return base path
          if (previousStep.slug === "buypanels") {
            return `${basePath}/`;
          }
          return `${basePath}/${previousStep.slug}`;
        }
        return `${basePath}/`;
      },
      setStepComplete: (slug: string) => {
        const { steps } = get();
        const updatedSteps = steps.map((step) =>
          step.slug === slug ? { ...step, isCompleted: true } : step
        );
        set({ steps: updatedSteps });
      },
      setStepIncomplete: (slug: string) => {
        const { steps } = get();
        const updatedSteps = steps.map((step) =>
          step.slug === slug ? { ...step, isCompleted: false } : step
        );
        set({ steps: updatedSteps });
      },
      getStepNumber: (slug: string) => {
        const { steps } = get();
        const step = steps.find((step) => step.slug === slug);
        return step?.number ?? 1;
      },

      // Cart state
      reset: () => {
        set({ data: {}, steps: steps, createdAt: new Date() });
      },
      createdAt: new Date(),
      _hasHydrated: false,
      setHasHydrated: (state) => {
        set({
          _hasHydrated: state,
        });
      },
      // Billing option defaults
      billingOption: "oneoff",
      setBillingOption: (option: "oneoff" | "recurring") =>
        set((state) => ({ ...state, billingOption: option })),

      // Payment frequency state
      paymentType: "once",
      setPaymentType: (type: "once" | "recurring") =>
        set({ paymentType: type, billingOption: type === "recurring" ? "recurring" : "oneoff" }),
      resetPaymentFrequency: () =>
        set({
          paymentType: "once",
          billingOption: "oneoff",
          frequency: "",
          endCondition: "no-end-date",
          endDate: "",
          paymentCount: "",
        }),
      frequency: "",
      setFrequency: (frequency: string) => set({ frequency }),
      endCondition: "no-end-date",
      setEndCondition: (condition: "no-end-date" | "certain-date" | "number-of-payments") => set({ endCondition: condition }),
      endDate: "",
      setEndDate: (date: string) => set({ endDate: date }),
      paymentCount: "",
      setPaymentCount: (count: string) => set({ paymentCount: count }),
    }),
    {
      name: "buy-panels-store",
      version: 2,
      storage: createJSONStorage(() => localStorage),
      migrate: (persistedState) => {
        const state = persistedState as Partial<BuyPanelsStore> & {
          createdAt?: string | Date;
        };

        return {
          ...state,
          steps,
          createdAt: state.createdAt ? new Date(state.createdAt) : new Date(),
        };
      },
      onRehydrateStorage: (state) => {
        return (storedState) => {
          if (!storedState?.createdAt) {
            state.reset();
            return;
          }

          const now = new Date();
          const createdAt = new Date(storedState?.createdAt ?? now);

          const isExpired =
            now.getTime() - createdAt.getTime() > 1000 * 60 * 60 * 6; // 6 hours

          if (isExpired) {
            state.reset();
          }

          state.setHasHydrated(true);
        };
      },
    }
  )
  )
);

useBuyPanelsStore.subscribe(
  (state) => state.data.order?.items,
  (items) => {
    if (!Array.isArray(items)) {
      try {
        setOrderUnitsCookie(0);
      } catch (e) {}
      return;
    }

    const itemCount = items.reduce((sum: number, it: any) => {
      const qty = Number(it?.units ?? it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
      return sum + qty / 100;
    }, 0);

    try {
      setOrderUnitsCookie(itemCount);
    } catch (e) {
    }
  },
  {fireImmediately: true}
);
